import { VideoGroup } from "@/stores/clip/class/VideoTrack";
import { VideoTrack } from "@/stores/clipEditor/class/VideoTrack";
import appStore from "@/stores/appStore";
import {
  CamelCasedProperties,
  SnakeCasedProperties,
} from "./tsUtils/typeTranslate";
import { AudioGroup } from "@/stores/clip/class/AudioTrack";
import { AudioTrack } from "@/stores/clipEditor/class/AudioTrack";
import { TrackLineItem } from "@/stores/clip/class/Track";
import { TrackLineItem as TrackLineEditorItem } from "@/stores/clipEditor/class/Track";

function snakeToCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export const convertKeysToCamelCase = <T extends Object>(
  obj: T
): CamelCasedProperties<T> => {
  return Object.keys(obj).reduce((acc, key) => {
    const camelCaseKey = snakeToCamelCase(key);
    const value = obj[key as keyof T];

    (acc as any)[camelCaseKey as keyof CamelCasedProperties<T>] =
      typeof value === "object" && value !== null && !Array.isArray(value)
        ? convertKeysToCamelCase(value)
        : value;

    return acc;
  }, {} as CamelCasedProperties<T>);
};

function camelToSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export const convertKeysToSnakeCase = <T extends Object>(
  obj: T
): SnakeCasedProperties<T> => {
  return Object.keys(obj).reduce((acc, key) => {
    const snakeCaseKey = camelToSnakeCase(key);
    const value = obj[key as keyof T];

    (acc as any)[snakeCaseKey] =
      typeof value === "object" && value !== null && !Array.isArray(value)
        ? convertKeysToSnakeCase(value)
        : value;

    return acc;
  }, {} as SnakeCasedProperties<T>);
};

/**
 *  时间格式化
 * */
export function formatTime(time: number) {
  let second = Math.ceil(time / 1000);
  const s = second % 60;
  second = Math.floor(second / 60);
  const m = second % 60;
  second = Math.floor(second / 60);
  const h = second % 60;
  return {
    s,
    m,
    h,
    str: `${h === 0 ? "" : `${h < 10 ? "0" : ""}${h}:`}${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`,
  };
}

// 生成 16 进制指定长度的字符串
export function getRandom(len: number) {
  return Math.floor((1 + Math.random()) * 16 ** len)
    .toString(16)
    .substring(1);
}

export function getId(prefix = "t") {
  return `${prefix ? `${prefix}-` : ""}${getRandom(5)}${getRandom(3)}-${getRandom(4)}`;
}

export function isVideo(type: string) {
  return type === "video";
}

/**
 * 精确计时器
 * @param callback
 * @param interval
 * @returns
 */
export function preciseInterval(callback: () => void, interval: number) {
  let expected = performance.now() + interval;
  let stop = false;

  function step(timestamp: number) {
    if (stop) return;

    if (timestamp >= expected) {
      callback();
      // 累积期望的时间，以保持精确的间隔
      expected += interval;
    }

    requestAnimationFrame(step);
  }

  requestAnimationFrame(step);

  // 返回一个对象包含取消方法
  return {
    cancel: () => {
      stop = true;
    },
  };
}

export function formatPlayerTime(frameCount: number) {
  console.log("=-===========", frameCount);

  // 计算总秒数
  const totalSeconds = Math.floor(frameCount / 1000);
  console.log("=-===========", totalSeconds);

  // 计算分钟和秒
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = totalSeconds % 60;
  // 格式化为两位数
  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${minutes}:${formattedSeconds}`;
}

export function isOfCanPlayType(
  value: unknown
): value is VideoGroup | AudioGroup {
  return value instanceof VideoGroup || value instanceof AudioGroup;
}

export function isOfCanPlayEditorType(
  value: unknown
): value is VideoTrack | AudioTrack {
  return value instanceof VideoTrack || value instanceof AudioTrack;
}

type TypeGuard<T> = (value: unknown) => value is T;

export const getCurrentTrackItemList = <T>(
  trackList: TrackLineItem[],
  currentFrame: number,
  isOfType: TypeGuard<T>
): T[] => {
  const trackItems: T[] = [];
  trackList.forEach(({ groups }) => {
    groups.forEach((trackItem) => {
      const { start, end } = trackItem;
      if (start <= currentFrame && end >= currentFrame && isOfType(trackItem)) {
        trackItems.push(trackItem);
      }
    });
  });
  return trackItems;
};

export const getCurrentEditorTrackItemList = <T>(
  trackList: TrackLineEditorItem[],
  currentFrame: number,
  isOfType: TypeGuard<T>
): T[] => {
  const trackItems: T[] = [];
  trackList.forEach(({ list }) => {
    list.forEach((trackItem) => {
      const { start, end } = trackItem;
      if (start <= currentFrame && end >= currentFrame && isOfType(trackItem)) {
        trackItems.push(trackItem);
      }
    });
  });
  return trackItems;
};

export const copyText = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export function formatNumberShort(num) {
  if (num >= 100000000) {
    return (num / 100000000).toFixed(1) + "亿";
  }
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + "w";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "k";
  }
  return num;
}

export function enumRatio(val) {
  switch (val) {
    case "3:4":
      return {
        w: 360,
        h: 480,
      };
      break;
    case "4:3":
      return {
        w: 360,
        h: (360 / 4) * 3,
      };
      break;
    case "16:9":
      return {
        w: 360,
        h: (360 / 16) * 9,
      };
      break;
    case "9:16":
      return {
        w: 360,
        h: 640,
      };
      break;
    case "1:1":
      return {
        w: 360,
        h: 360,
      };
      break;
    default:
      return {
        w: 360,
        h: 640,
      };
  }
}
export const getSnapInfo = (url) => {
  console.log("getSnapInfo-------", url);
  if (!url) return;
  const match = url.match(/_(\d+)x(\d+)_([\d]+)/);

  if (match) {
    const width = parseInt(match[1], 10); // 60
    const height = parseInt(match[2], 10); // 106
    const count = parseInt(match[3], 10); // 200

    return {
      width,
      height,
      count,
    };
  } else {
    console.error("链接格式错误");
  }
};

export const frameCountToMs = (frameCount) => {
  return (frameCount / 30) * 1000;
};


export function pageData(items, pageSize) {
  const total = items.length;
  const pageCount = Math.ceil(total / pageSize);
  return {
    total,
    pageSize,
    pageCount,
    getData: (page) => {
      if (page > pageCount) {
        page = pageCount;
      }
      if (page < 1) {
        page = 1;
      }
      const pageNo = page - 1;
      const startIndex = pageNo * pageSize;
      let endIndex = startIndex + pageSize;
      if (endIndex > total) {
        endIndex = total;
      }
      const data = items.slice(startIndex, endIndex);
      return data;
    },
  };
}


export function transMediaList (data) {
  if (!data) return []

  if (Array.isArray(data)) {
    return data.map((item) => {
      const basicInfo = item.MediaBasicInfo
      const fileBasicInfo = item.FileInfoList[0].FileBasicInfo
      const mediaId = basicInfo.MediaId
      const result = {
        mediaId,
        mediaType: undefined,
        video: undefined,
        audio:undefined,
        image:undefined,
      }
      const mediaType = basicInfo.MediaType
      result.mediaType = mediaType

      if (mediaType === 'video') {
        result.video = {
          title: fileBasicInfo.FileName,
          duration: Number(fileBasicInfo.Duration),
          // 源视频的宽高、码率等数据，用于推荐合成数据，不传入或是0时无推荐数据
          width: Number(fileBasicInfo.Width) || 0,
          height: Number(fileBasicInfo.Height) || 0,
          bitrate: Number(fileBasicInfo.Bitrate) || 0,
          coverUrl: basicInfo.CoverURL
        }
        const spriteImages = basicInfo.SpriteImages
        if (spriteImages) {
          try {
            const spriteArr = JSON.parse(spriteImages)
            const sprite = spriteArr[0]
            const config = JSON.parse(sprite.Config)
            result.video.spriteConfig = {
              num: config.Num,
              lines: config.SpriteSnapshotConfig?.Lines,
              cols: config.SpriteSnapshotConfig?.Columns,
              cellWidth: config.SpriteSnapshotConfig?.CellWidth,
              cellHeight: config.SpriteSnapshotConfig?.CellHeight
            }
            result.video.sprites = sprite.SnapshotUrlList
          } catch (e) {
            console.log(e)
          }
        }
      } else if (mediaType === 'audio') {
        result.audio = {
          title: fileBasicInfo.FileName,
          duration: Number(fileBasicInfo.Duration),
          coverURL: '' // 您可以给音频文件一个默认的封面图
        }
      } else if (mediaType === 'image') {
        result.image = {
          title: fileBasicInfo.FileName,
          coverUrl: fileBasicInfo.FileUrl,
          // 图片的宽高等数据，用于推荐合成数据，不传入或是0时无推荐数据
          width: Number(fileBasicInfo.Width) || 0,
          height: Number(fileBasicInfo.Height) || 0
        }
      }

      return result
    })
  } else {
    return [data]
  }
}

// export function request (action, params) {
//   return axios.post('/openApiPost', {
//     ...params,
//     Action: action
//   })
// }
